import clsx from 'clsx';

import { Modal, Button, Form, InputGroup } from 'react-bootstrap';
import Spinner from 'react-bootstrap/Spinner';
import Select from 'react-select';

import { Item } from '@/types';
import { selectInGroup, selectCustomStyle } from '@/config/model';

import {
  AIModuleEntity,
  AI_MODULE_ITEM_ACTIONS as ACTIONS,
  AI_MODULE_ITEM_ALGORITHMS as ALGOS,
} from '../types';

import { Items4CompanyTable } from '../components/Items4ComTable';
import { useItemsUnderAICompany } from '../hooks/use-items-edit-hook';

type RBModalProps = {
  show: boolean;
  aiModuleSelected: AIModuleEntity;
  items: (Item | undefined)[] | undefined;
  handleClose: () => void;
};

/**
 * == Update Items for AI Module towards specific company ==
 * @returns
 */
export const ItemsEditAIModule = ({
  show,
  items,
  aiModuleSelected,
  handleClose,
}: RBModalProps) => {
  const { id, ai_module_key } = aiModuleSelected;

  const {
    isLoading,
    formVisible,
    payload,
    itemRows,
    itemOptions,
    showExpandedFields,
    showForm,
    hideForm,
    selectCompanyToQuery,
    handleSubmitToEditAIModule,
    handleCompanySelect,
    handleItemSelect,
    handleActionSelect,
    handleExpandedItemInput,
    handleAlgorithmSelect,
    handleCorrectionFactorInput,
    handleItemRowDelete,
    handleRestoreAndClose,
  } = useItemsUnderAICompany(aiModuleSelected, items, handleClose);

  return (
    <Modal className="modal-ai-items" as={Modal.Dialog} centered show={show}>
      <Modal.Header>
        <Modal.Title className="h6">
          {formVisible ? 'Add Item' : 'Edit Items'} for AI Module:{' '}
          {ai_module_key}
        </Modal.Title>
        <Button
          variant="close"
          aria-label="Close"
          onClick={handleRestoreAndClose}
        />
      </Modal.Header>

      <Modal.Body>
        {/* == table view == */}
        <Items4CompanyTable
          formVisible={formVisible}
          payload={payload}
          aiModuleSelected={aiModuleSelected}
          itemRows={itemRows}
          showForm={showForm}
          onCompanyChange={selectCompanyToQuery}
          handleClose={handleRestoreAndClose}
          handleItemDelete={handleItemRowDelete}
        />
        {/* == form view == */}
        <Form
          onSubmit={handleSubmitToEditAIModule}
          className={clsx(formVisible ? '' : 'd-lg-none')}
        >
          {/* company select */}
          <Form.Group className="mb-2">
            <Form.Label>Companies for AI module</Form.Label>
            <Form.Select
              name="company_id"
              value={payload.company_id}
              aria-label="select company"
              onChange={handleCompanySelect}
            >
              <option>Select a company to get started...</option>
              {aiModuleSelected.companies?.map((c) => (
                <option key={c.company_id} value={c.company_id}>
                  {c.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          {/* form row - 1: item | action */}
          <Form.Group className="mb-2">
            <Form.Label>Source Item & Action</Form.Label>
            <InputGroup className="d-flex gap-half-1">
              {/* == item catalogue select list == */}
              <Select
                placeholder="Select or query a item..."
                name="item_id"
                options={itemOptions}
                classNamePrefix="select"
                className="flex-even"
                styles={selectInGroup}
                onChange={handleItemSelect}
              />
              <Form.Select
                name="action"
                className="flex-even"
                aria-label="Default action ..."
                value={payload.action}
                onChange={handleActionSelect}
              >
                <option value="">Select action...</option>
                {ACTIONS.map((act) => (
                  <option key={act} value={act}>
                    {act}
                  </option>
                ))}
              </Form.Select>
            </InputGroup>
          </Form.Group>
          {/* form row - 2: expend item == optional fields */}
          <Form.Group
            className={clsx('mb-2', showExpandedFields ? '' : 'd-lg-none')}
          >
            <Form.Label>Expanded Item ID</Form.Label>
            <Select
              placeholder="Select or query a target item different from above one..."
              name="expanded_item"
              options={itemOptions}
              classNamePrefix="select"
              className="flex-even"
              styles={selectCustomStyle}
              onChange={handleExpandedItemInput}
            />
          </Form.Group>
          {/* form row - 3: formula | correction_factor == optional fields */}
          <Form.Group
            className={clsx('mb-2', showExpandedFields ? '' : 'd-lg-none')}
          >
            <Form.Label>Algorithm & Correction Factor</Form.Label>
            <InputGroup>
              <Form.Select
                name="formula"
                value={payload.formula}
                aria-label="Default algorithm ..."
                onChange={handleAlgorithmSelect}
              >
                <option>Open this select algorithm</option>
                {ALGOS.map((alg) => (
                  <option key={alg} value={alg}>
                    {alg}
                  </option>
                ))}
              </Form.Select>
              <Form.Control
                name="correction_factor"
                type="number"
                value={payload.correction_factor}
                step={0.0000000001}
                placeholder="factor value number"
                onChange={handleCorrectionFactorInput}
              />
            </InputGroup>
          </Form.Group>
          {/* save it */}
          <Form.Group className="mt-4 d-flex justify-content-end gap-3 border-top pt-3">
            <Button type="button" variant="secondary" onClick={handleClose}>
              Close
            </Button>
            <Button
              type="button"
              variant="dark"
              onClick={hideForm}
              className={clsx(formVisible ? '' : 'd-lg-none')}
            >
              Show Items
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={!payload.company_id}
            >
              {isLoading && (
                <>
                  <Spinner size="sm" animation="border" />
                  ......
                </>
              )}
              {!isLoading && `Save`}
            </Button>
          </Form.Group>
        </Form>
      </Modal.Body>
    </Modal>
  );
};
